import { useEffect, useState } from 'react';
import { verifyMerchantPass } from '../api/merchantPass';
import { getActiveStore } from '../api/client';
import { passStore, takePass, type HeldPass } from './merchantPass';

/** The bar's state and the two ways out of it. */
export interface MerchantBar {
  held: HeldPass | null;
  hide: () => void;
  exit: () => void;
}

// Reports whether this page is inside a frame — the builder's preview — where the bar never shows.
function embedded(): boolean {
  try { return window.self !== window.top; } catch { return true; }
}

// Holds the merchant's pass for this store: one arriving in the address is checked with the store and kept,
// one already kept is used until it expires; "Hide for this visit" hides it, "Stop showing on this browser" forgets it.
export function useMerchantBar(): MerchantBar {
  const store = getActiveStore()?.slug ?? '';
  const keep = passStore(store);
  const [held, setHeld] = useState<HeldPass | null>(() => (embedded() || keep.hidden() ? null : keep.load()));

  useEffect(() => {
    if (embedded()) return;
    const { token, cleaned } = takePass(window.location.href);
    if (!token) return;
    window.history.replaceState(window.history.state, '', cleaned);
    verifyMerchantPass(token).then((check) => {
      if (!check.valid) return;
      const fresh = { token, expiresAt: check.expiresAt, adminUrl: check.adminUrl, theme: check.theme };
      keep.save(fresh);
      if (!keep.hidden()) setHeld(fresh);
    }).catch(() => undefined);
    // The address is read once per page load; the store does not change under a running page.
  }, []);

  return {
    held,
    hide: () => { keep.hide(); setHeld(null); },
    exit: () => { keep.drop(); setHeld(null); }
  };
}
